import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { projectsAPI, proposalsAPI, paymentsAPI } from '../services/api'
import ChatDialog from '../components/ChatDialog'
import PublicHeader from '../components/PublicHeader'
import {
  ArrowLeft, Calendar, MapPin, Euro, Building,
  Clock, ChevronRight, Send, User, Target,
  Package, FileText, AlertCircle, Users,
  MessageCircle, Award, Eye, CheckCircle, XCircle, Globe
} from 'lucide-react'

const ProjectDetailPage = ({ projectId: propProjectId, onNavigate, onBack } = {}) => {
  const params = useParams()
  const id = propProjectId || params.id
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // Reageren vereist minimaal één portfolio-video (afgedwongen in
  // routes/proposals.js). Vooraf tonen, anders schrijft iemand een motivatie
  // die alsnog wordt geweigerd.
  const heeftPortfolioVideo = (user?.portfolio || []).some(item => item?.videoUrl)

  const gaNaarPortfolio = () => {
    if (onNavigate) onNavigate('settings', null, 'portfolio')
    else navigate('/profile?tab=portfolio')
  }

  // Helper for navigation that works both standalone and embedded
  const handleNav = (path) => {
    if (onNavigate) {
      // Map paths to dashboard view names
      if (path.match(/^\/creators\/(.+)$/)) {
        const creatorId = path.match(/^\/creators\/(.+)$/)[1]
        onNavigate('creator-detail', creatorId)
      } else if (path.match(/^\/projects\/(.+)\/proposals$/)) {
        const projId = path.match(/^\/projects\/(.+)\/proposals$/)[1]
        onNavigate('project-proposals', projId)
      } else if (path.match(/^\/projects\/(.+)\/workspace$/)) {
        const projId = path.match(/^\/projects\/(.+)\/workspace$/)[1]
        onNavigate('project-workspace', projId)
      } else if (path === '/projects/create') {
        onNavigate('create-project')
      } else {
        navigate(path)
      }
    } else {
      navigate(path)
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigate(-1)
    }
  }
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showProposalForm, setShowProposalForm] = useState(false)
  const [proposalData, setProposalData] = useState({
    coverLetter: '',
    price: '',
    deliveryTime: '',
    previousWork: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [proposals, setProposals] = useState([])
  const [totalProposals, setTotalProposals] = useState(0)
  const [proposalViewMode, setProposalViewMode] = useState('public')
  const [loadingProposals, setLoadingProposals] = useState(false)
  // Eigen voorstel van de ingelogde creator (uit GET /proposals/project/:id).
  // Bepaalt of "Stuur voorstel" getoond wordt of "je hebt al gereageerd".
  const [myProposal, setMyProposal] = useState(null)
  const [showChatDialog, setShowChatDialog] = useState(false)
  const [selectedCreator, setSelectedCreator] = useState(null)

  useEffect(() => {
    fetchProject()
    fetchProposals()
  }, [id])

  const fetchProject = async () => {
    try {
      setLoading(true)
      const response = await projectsAPI.getProject(id)
      // Backend returns data in response.data.project
      setProject(response.data.project || response.data)
    } catch (error) {
      console.error('Error fetching project:', error)
      // 404 = door het bedrijf verwijderd/gesloten (notificaties en mails kunnen
      // er nog naar wijzen) — zeg dat, in plaats van een kale foutmelding.
      setError(error.response?.status === 404
        ? 'Deze opdracht is niet meer beschikbaar: het bedrijf heeft de opdracht verwijderd of gesloten.'
        : 'We konden deze opdracht niet laden. Probeer het later opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  const fetchProposals = async () => {
    try {
      setLoadingProposals(true)
      const response = await proposalsAPI.getProjectProposals(id)
      setProposals(response.data.proposals || [])
      setTotalProposals(response.data.totalProposals || response.data.pagination?.totalProposals || 0)
      setProposalViewMode(response.data.viewMode || 'public')
      setMyProposal(response.data.myProposal || null)
    } catch (error) {
      console.error('Error fetching proposals:', error)
      // Don't show error, just keep empty proposals
    } finally {
      setLoadingProposals(false)
    }
  }

  const handleStartChat = (creator) => {
    setSelectedCreator(creator)
    setShowChatDialog(true)
  }

  const handleAcceptProposal = async (proposalId) => {
    if (!confirm('Je legt deze creator vast en gaat door naar de aanbetaling. Je kunt daarna nog meer creators kiezen.')) return

    try {
      // Accepteren maakt een eigen (kind)project voor deze creator aan; de backend
      // geeft dat id terug als project.id — daarop loopt de aanbetaling.
      const acceptResp = await proposalsAPI.acceptProposal(proposalId)
      const targetId = acceptResp?.data?.project?.id || id
      const checkoutResp = await paymentsAPI.processDepositPayment(targetId)
      const checkoutUrl = checkoutResp?.data?.data?.checkoutUrl || checkoutResp?.data?.checkoutUrl
      if (checkoutUrl) {
        window.location.href = checkoutUrl
        return
      }
      alert('Voorstel geaccepteerd. Ga naar je opdracht om de aanbetaling te voldoen.')
      fetchProject()
      fetchProposals()
    } catch (error) {
      console.error('Error accepting proposal:', error)
      const data = error?.response?.data
      if (data?.billingIncomplete) {
        alert(data.message)
        navigate('/profile?tab=billing')
        return
      }
      alert(data?.message || 'Er is een fout opgetreden bij het accepteren van het voorstel')
    }
  }

  const handleRejectProposal = async (proposalId) => {
    const reason = prompt('Optioneel: Geef een reden voor afwijzing')
    if (reason === null) return // User cancelled

    try {
      await proposalsAPI.rejectProposal(proposalId, { reason: reason || '' })
      alert('Voorstel afgewezen')
      fetchProposals()
    } catch (error) {
      console.error('Error rejecting proposal:', error)
      alert('Er is een fout opgetreden bij het afwijzen van het voorstel')
    }
  }

  const handleProposalSubmit = async (e) => {
    e.preventDefault()

    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (user?.role !== 'creator') {
      alert('Alleen creators kunnen voorstellen indienen')
      return
    }

    if (submitting) return // dubbelklik/enter+klik → tweede POST kreeg "already submitted"

    try {
      setSubmitting(true)
      // Map frontend fields to backend expected fields
      const submissionData = {
        motivation: proposalData.coverLetter,
        approach: proposalData.previousWork || '',
        price: parseInt(proposalData.price),
        deliveryTimeInDays: parseInt(proposalData.deliveryTime)
      }

      const ingediend = await proposalsAPI.submitProposal(id, submissionData)
      alert('Voorstel succesvol ingediend!')
      setShowProposalForm(false)
      setMyProposal(ingediend.data?.proposal
        ? { id: ingediend.data.proposal.id || ingediend.data.proposal._id, status: 'submitted', submittedAt: new Date().toISOString() }
        : { status: 'submitted', submittedAt: new Date().toISOString() })
      setProposalData({
        coverLetter: '',
        price: '',
        deliveryTime: '',
        previousWork: ''
      })
    } catch (error) {
      console.error('Error submitting proposal:', error)

      // Better error handling
      if (error.response?.data?.code === 'PORTFOLIO_REQUIRED') {
        const naarPortfolio = window.confirm(
          `${error.response.data.message}\n\nNu een video toevoegen?`
        )
        if (naarPortfolio) gaNaarPortfolio()
      } else if (error.response?.data?.error === 'Proposal already exists') {
        alert('Je hebt al een voorstel ingediend voor deze opdracht. Je vindt het terug onder "Mijn voorstellen".')
        setShowProposalForm(false)
        setMyProposal(prev => prev || { status: 'submitted' })
      } else if (error.response && error.response.data && error.response.data.messages) {
        alert('Validatiefouten:\n' + error.response.data.messages.join('\n'));
      } else if (error.response && error.response.data && error.response.data.message) {
        alert('Fout: ' + error.response.data.message);
      } else {
        alert('Er is een fout opgetreden bij het indienen van je voorstel')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const contentTypeLabels = { video: 'Video', foto: 'Foto', beide: 'Video & Foto' }
  const contentStyleLabels = {
    unboxing: 'Unboxing', review: 'Review', tutorial: 'Tutorial', lifestyle: 'Lifestyle',
    testimonial: 'Testimonial', comparison: 'Vergelijking', 'before-after': 'Voor/Na', other: 'Overig'
  }
  const genderLabels = { male: 'Mannelijk', female: 'Vrouwelijk', 'non-binary': 'Non-binair', any: 'Geen voorkeur' }
  const industryLabels = {
    fashion: 'Mode', beauty: 'Beauty', 'food-beverage': 'Food & Beverage', tech: 'Tech',
    'health-wellness': 'Health & Wellness', travel: 'Reizen', automotive: 'Automotive',
    'home-garden': 'Wonen & Tuin', sports: 'Sport', education: 'Educatie', finance: 'Finance',
    entertainment: 'Entertainment', b2b: 'B2B', other: 'Overig'
  }
  const nicheLabels = {
    beauty: 'Beauty', fashion: 'Mode', fitness: 'Fitness', health: 'Gezondheid',
    food: 'Food', travel: 'Reizen', tech: 'Tech', gaming: 'Gaming', lifestyle: 'Lifestyle',
    parenting: 'Parenting', pets: 'Huisdieren', home: 'Wonen', diy: 'DIY',
    automotive: 'Automotive', sports: 'Sport', music: 'Muziek', art: 'Kunst',
    education: 'Educatie', business: 'Business', finance: 'Finance',
    sustainability: 'Duurzaamheid', other: 'Overig'
  }
  const urgencyLabels = { low: 'Laag', medium: 'Normaal', high: 'Hoog', urgent: 'Urgent' }
  // '9:16 (Reels/TikTok)' -> 'Reels/TikTok'
  const formatPlatform = (f) => {
    const m = typeof f === 'string' && f.match(/\(([^)]+)\)/)
    return m ? m[1] : f
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] text-[#0A0F0D] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#3EB489] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#475569]">Project laden...</p>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] text-[#0A0F0D] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Opdracht niet (meer) beschikbaar</h2>
          <p className="text-[#475569] mb-6 max-w-md mx-auto">{error}</p>
          {onNavigate ? (
            <button onClick={handleBack} className="inline-flex items-center px-5 py-2.5 rounded-xl bg-[#3EB489] text-white font-semibold hover:bg-[#35a07a]">
              Bekijk open opdrachten
            </button>
          ) : (
            <Link to="/projects" className="inline-flex items-center px-5 py-2.5 rounded-xl bg-[#3EB489] text-white font-semibold hover:bg-[#35a07a]">
              Bekijk open opdrachten
            </Link>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0F0D]">
      {/* Header - only show when standalone (not embedded in dashboard) */}
      {!onNavigate && <PublicHeader />}

      {/* Main Content */}
      <div className={`max-w-7xl mx-auto px-4 md:px-6 ${onNavigate ? 'pt-6 pb-20' : 'pt-6 md:pt-10 pb-28 lg:pb-20'}`}>
        {/* Back Button */}
        {onNavigate ? (
          <button onClick={handleBack} className="inline-flex items-center min-h-[44px] -my-2 text-[#475569] hover:text-[#3EB489] transition mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug
          </button>
        ) : (
          <Link to="/projects" className="inline-flex items-center min-h-[44px] -my-2 text-[#475569] hover:text-[#3EB489] transition mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar projecten
          </Link>
        )}

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Header */}
            <div className="relative">
              <div className="relative p-6 md:p-8 rounded-3xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                <h1 className="text-2xl md:text-3xl font-bold mb-4">{project.title}</h1>

                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-2 text-[#475569]">
                    <Building className="w-4 h-4" />
                    <span>{project.owner?.companyName || project.owner?.company || 'Bedrijf'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#475569]">
                    <MapPin className="w-4 h-4" />
                    <span>{project.desiredCreatorProfile?.location?.country || 'Nederland'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#475569]">
                    <Calendar className="w-4 h-4" />
                    <span>Deadline: {new Date(project.deadline).toLocaleDateString('nl-NL')}</span>
                  </div>
                  {project.productInfo?.website && (
                    <a
                      href={project.productInfo.website}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="flex items-center gap-2 min-w-0 max-w-full text-[#3EB489] hover:text-[#2A7E5F] hover:underline transition"
                    >
                      <Globe className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">{project.productInfo.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                    </a>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {project.contentType && (
                    <span className="inline-block px-3 py-1 bg-[#ECFDF5] text-[#3EB489] rounded-full text-sm font-medium">
                      {contentTypeLabels[project.contentType] || project.contentType}
                    </span>
                  )}
                  {project.industry && (
                    <span className="inline-block px-3 py-1 bg-purple-50 text-[#7c3aed] rounded-full text-sm font-medium">
                      {industryLabels[project.industry] || project.industry}
                    </span>
                  )}
                  {(project.urgency === 'high' || project.urgency === 'urgent') && (
                    <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                      Spoed: {urgencyLabels[project.urgency]}
                    </span>
                  )}
                  {project.status && project.status !== 'open' && (
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                      {project.status}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Trust Signals — bedrijf-betrouwbaarheid voor creators */}
            {project.owner && (project.owner.kvkVerified || project.owner.emailVerified || project.owner.reputation) && (
              <div className="relative">
                <div className="relative p-5 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-[#0A0F0D]">Bedrijfsbetrouwbaarheid</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {project.owner.kvkVerified && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-medium">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        KvK geverifieerd
                      </span>
                    )}
                    {project.owner.emailVerified && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-medium">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        E-mail geverifieerd
                      </span>
                    )}
                    {(project.owner.reputation?.briefsPosted || 0) > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#ECFDF5] text-[#2A7E5F] border border-[#D1FAE5] rounded-full text-xs font-medium">
                        {project.owner.reputation.briefsPosted} brief{project.owner.reputation.briefsPosted === 1 ? '' : 's'} geplaatst
                      </span>
                    )}
                    {(project.owner.reputation?.depositsCompleted || 0) > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#ECFDF5] text-[#2A7E5F] border border-[#D1FAE5] rounded-full text-xs font-medium">
                        {project.owner.reputation.depositsCompleted} aanbetaling{project.owner.reputation.depositsCompleted === 1 ? '' : 'en'} voldaan
                      </span>
                    )}
                    {project.owner.reputation?.followThroughRate !== null && project.owner.reputation?.followThroughRate !== undefined && (
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                        project.owner.reputation.followThroughRate >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        project.owner.reputation.followThroughRate >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }`}>
                        {project.owner.reputation.followThroughRate}% follow-through na acceptance
                      </span>
                    )}
                    {project.owner.memberSince && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-600 border border-[#E5E7EB] rounded-full text-xs font-medium">
                        Lid sinds {new Date(project.owner.memberSince).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            <div className="relative">
              <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-[#3EB489]" />
                  Beschrijving
                </h2>
                <p className="text-[#0A0F0D] leading-relaxed whitespace-pre-wrap break-words">
                  {project.description}
                </p>
              </div>
            </div>

            {/* Opdracht details */}
            {(project.contentType || (project.contentFormat?.length > 0) || project.contentStyle || (project.tags?.length > 0)) && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-[#3EB489]" />
                    Wat we zoeken
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {project.contentType && (
                      <div>
                        <p className="text-xs text-[#475569] mb-1">Type content</p>
                        <p className="text-[#0A0F0D] font-medium">{contentTypeLabels[project.contentType] || project.contentType}</p>
                      </div>
                    )}
                    {project.contentStyle && (
                      <div>
                        <p className="text-xs text-[#475569] mb-1">Stijl</p>
                        <p className="text-[#0A0F0D] font-medium">{contentStyleLabels[project.contentStyle] || project.contentStyle}</p>
                      </div>
                    )}
                    {project.contentFormat?.length > 0 && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-[#475569] mb-1.5">Formaten / platforms</p>
                        <div className="flex flex-wrap gap-2">
                          {project.contentFormat.map((f, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-[#7c3aed] border border-purple-100">
                              {formatPlatform(f)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {project.tags?.length > 0 && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-[#475569] mb-1.5">Tags</p>
                        <div className="flex flex-wrap gap-2">
                          {project.tags.map((t, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-[#475569]">
                              #{t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Gewenst creator-profiel */}
            {project.desiredCreatorProfile && (
              (project.desiredCreatorProfile.gender && project.desiredCreatorProfile.gender !== 'any') ||
              project.desiredCreatorProfile.ageRange?.min || project.desiredCreatorProfile.ageRange?.max ||
              project.desiredCreatorProfile.minFollowers ||
              (project.desiredCreatorProfile.niches?.length > 0) ||
              (project.desiredCreatorProfile.location?.cities?.length > 0) ||
              project.desiredCreatorProfile.specificRequirements
            ) && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-[#3EB489]" />
                    Wie zoeken we?
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {project.desiredCreatorProfile.gender && project.desiredCreatorProfile.gender !== 'any' && (
                      <div>
                        <p className="text-xs text-[#475569] mb-1">Geslacht</p>
                        <p className="text-[#0A0F0D] font-medium">{genderLabels[project.desiredCreatorProfile.gender]}</p>
                      </div>
                    )}
                    {(project.desiredCreatorProfile.ageRange?.min || project.desiredCreatorProfile.ageRange?.max) && (
                      <div>
                        <p className="text-xs text-[#475569] mb-1">Leeftijd</p>
                        <p className="text-[#0A0F0D] font-medium">
                          {project.desiredCreatorProfile.ageRange?.min || '?'}–{project.desiredCreatorProfile.ageRange?.max || '?'} jaar
                        </p>
                      </div>
                    )}
                    {project.desiredCreatorProfile.minFollowers > 0 && (
                      <div>
                        <p className="text-xs text-[#475569] mb-1">Minimaal volgers</p>
                        <p className="text-[#0A0F0D] font-medium">{Number(project.desiredCreatorProfile.minFollowers).toLocaleString('nl-NL')}+</p>
                      </div>
                    )}
                    {project.desiredCreatorProfile.location?.cities?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#475569] mb-1">Locatie</p>
                        <p className="text-[#0A0F0D] font-medium">
                          {project.desiredCreatorProfile.location.cities.join(', ')}
                          {project.shootLocation?.mode === 'op_locatie' && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-[#3EB489] border border-[#3EB489]/40">
                              Opname op locatie
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                    {project.desiredCreatorProfile.niches?.length > 0 && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-[#475569] mb-1.5">Niches</p>
                        <div className="flex flex-wrap gap-2">
                          {project.desiredCreatorProfile.niches.map((n, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#ECFDF5] text-[#2A7E5F] border border-[#D1FAE5]">
                              {nicheLabels[n] || n}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {project.desiredCreatorProfile.specificRequirements && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-[#475569] mb-1">Specifieke eisen</p>
                        <p className="text-[#0A0F0D] whitespace-pre-wrap">{project.desiredCreatorProfile.specificRequirements}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Richtlijnen: do's & don'ts */}
            {project.dos_and_donts && (
              (project.dos_and_donts.dos?.length > 0) || (project.dos_and_donts.donts?.length > 0) ||
              (project.dos_and_donts.mustInclude?.length > 0) || (project.dos_and_donts.keywords?.length > 0)
            ) && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-[#3EB489]" />
                    Richtlijnen
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {project.dos_and_donts.dos?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-emerald-700 mb-2 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1.5" /> Wel doen
                        </p>
                        <ul className="space-y-1.5">
                          {project.dos_and_donts.dos.map((d, index) => (
                            <li key={index} className="text-sm text-[#0A0F0D] flex items-start">
                              <span className="text-emerald-500 mr-2">•</span>{d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {project.dos_and_donts.donts?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-red-600 mb-2 flex items-center">
                          <XCircle className="w-4 h-4 mr-1.5" /> Niet doen
                        </p>
                        <ul className="space-y-1.5">
                          {project.dos_and_donts.donts.map((d, index) => (
                            <li key={index} className="text-sm text-[#0A0F0D] flex items-start">
                              <span className="text-red-400 mr-2">•</span>{d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {project.dos_and_donts.mustInclude?.length > 0 && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-[#475569] mb-1.5">Moet bevatten</p>
                        <div className="flex flex-wrap gap-2">
                          {project.dos_and_donts.mustInclude.map((m, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              {m}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {project.dos_and_donts.keywords?.length > 0 && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-[#475569] mb-1.5">Keywords / hashtags</p>
                        <div className="flex flex-wrap gap-2">
                          {project.dos_and_donts.keywords.map((k, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-[#475569]">
                              #{k}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Product */}
            {project.productInfo && (project.productInfo.name || project.productInfo.willSendProduct || project.productInfo.description) && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <Package className="w-5 h-5 mr-2 text-[#3EB489]" />
                    Product
                  </h2>
                  {project.productInfo.name && (
                    <p className="text-[#0A0F0D] font-medium mb-1">{project.productInfo.name}</p>
                  )}
                  {project.productInfo.description && (
                    <p className="text-[#475569] text-sm mb-3 whitespace-pre-wrap">{project.productInfo.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {project.productInfo.willSendProduct && !project.productShipping?.returnShipment?.required && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3.5 h-3.5" /> Gratis product wordt opgestuurd
                      </span>
                    )}
                    {project.productInfo.willSendProduct && project.productShipping?.returnShipment?.required && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-[#475569] border border-[#E5E7EB] rounded-full text-xs font-medium">
                        <Package className="w-3.5 h-3.5" /> Product wordt opgestuurd en na afloop geretourneerd
                      </span>
                    )}
                    {project.productInfo.price > 0 && (
                      <span className="inline-flex items-center px-3 py-1.5 bg-gray-50 text-gray-600 border border-[#E5E7EB] rounded-full text-xs font-medium">
                        Productwaarde: €{Number(project.productInfo.price).toLocaleString('nl-NL')}
                      </span>
                    )}
                  </div>
                  {project.productInfo.willSendProduct && project.productShipping?.returnShipment?.required && (
                    <p className="mt-3 text-xs text-[#475569]">
                      Het product is uitsluitend bedoeld voor het maken van de content, blijft eigendom van het bedrijf en wordt na afloop van de samenwerking teruggestuurd.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Proposals Section - Only visible for project owner */}
            {user && project && (user.role === 'bedrijf' || user.role === 'agency') && project.owner?._id === user.id && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <h2 className="text-xl font-bold mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="w-5 h-5 mr-2 text-[#3EB489]" />
                      Creators die hebben gereageerd ({proposals.length})
                    </div>
                    {proposals.some(p => !p.companyViewed) && (
                      <span className="px-2 py-1 bg-[#ECFDF5] text-[#3EB489] rounded-full text-xs">
                        Nieuw
                      </span>
                    )}
                  </h2>

                  {loadingProposals ? (
                    <div className="text-center py-8">
                      <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  ) : proposals.length === 0 ? (
                    <p className="text-[#475569]">Nog geen creators hebben gereageerd op dit project.</p>
                  ) : (
                    <div className="space-y-4">
                      {proposals.map((proposal) => (
                        <div key={proposal.id} className="p-4 rounded-xl bg-[#FAFAF7] border border-[#E5E7EB] hover:border-[#3EB489]/50 transition">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {proposal.creator.profilePictureUrl ? (
                                <img
                                  src={proposal.creator.profilePictureUrl}
                                  alt={proposal.creator.name}
                                  className="w-12 h-12 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-[#3EB489] flex items-center justify-center">
                                  <User className="w-6 h-6 text-white" />
                                </div>
                              )}
                              <div>
                                <h4 className="font-semibold">{proposal.creator.name}</h4>
                                {proposal.creator.bio ? (
                                  <p className="text-xs text-[#475569] mb-1 line-clamp-2">{proposal.creator.bio}</p>
                                ) : (
                                  <p className="text-xs text-[#475569]/50 mb-1">Geen bio beschikbaar</p>
                                )}
                                <div className="flex items-center gap-2 text-xs text-[#475569]">
                                  {proposal.creator.location?.city && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {proposal.creator.location.city}
                                    </span>
                                  )}
                                  {proposal.creator.stats?.completedProjects > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Award className="w-3 h-3" />
                                      {proposal.creator.stats.completedProjects} projecten
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {!proposal.companyViewed && (
                              <span className="px-2 py-1 bg-[#ECFDF5] text-[#3EB489] rounded text-xs">
                                Nieuw
                              </span>
                            )}
                          </div>

                          {proposal.motivation && (
                            <div className="mb-3">
                              <p className="text-xs text-[#475569] mb-1">Motivatie:</p>
                              <p className="text-[#0A0F0D] text-sm line-clamp-3">
                                {proposal.motivation}
                              </p>
                            </div>
                          )}

                          {proposal.creator.niches && proposal.creator.niches.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {proposal.creator.niches.slice(0, 3).map((niche, idx) => (
                                <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs">
                                  {niche}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[#E5E7EB]">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                              {proposal.finalPrice && proposal.finalPrice > 0 ? (
                                <span className="font-semibold text-[#3EB489]">
                                  €{proposal.finalPrice}
                                </span>
                              ) : (
                                <span className="text-[#475569]">Prijs: Onderhandelbaar</span>
                              )}
                              {proposal.deliveryTimeInDays && (
                                <span className="text-[#475569]">
                                  • {proposal.deliveryTimeInDays} dagen
                                </span>
                              )}
                              <span className="text-[#475569]">
                                • {new Date(proposal.submittedAt).toLocaleDateString('nl-NL')}
                              </span>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {onNavigate ? (
                                <button
                                  onClick={() => onNavigate('creator-detail', proposal.creator.id)}
                                  className="min-h-[40px] px-3 py-1.5 bg-white border border-[#E5E7EB] hover:border-[#0A0F0D] rounded-lg text-sm transition flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  Profiel
                                </button>
                              ) : (
                                <Link
                                  to={`/creators/${proposal.creator.id}`}
                                  className="min-h-[40px] px-3 py-1.5 bg-white border border-[#E5E7EB] hover:border-[#0A0F0D] rounded-lg text-sm transition flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  Profiel
                                </Link>
                              )}
                              <button
                                onClick={() => handleStartChat(proposal.creator)}
                                className="min-h-[40px] px-3 py-1.5 bg-white border border-[#E5E7EB] hover:border-[#0A0F0D] rounded-lg text-sm transition flex items-center gap-1"
                              >
                                <MessageCircle className="w-3 h-3" />
                                Chat
                              </button>
                              {(proposal.status === 'pending' || proposal.status === 'submitted') && (
                                <>
                                  <button
                                    onClick={() => handleAcceptProposal(proposal.id)}
                                    className="min-h-[40px] px-3 py-1.5 bg-[#3EB489] hover:bg-[#359E78] rounded-lg text-sm transition flex items-center gap-1 text-white"
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                    Accepteer
                                  </button>
                                  <button
                                    onClick={() => handleRejectProposal(proposal.id)}
                                    className="min-h-[40px] px-3 py-1.5 bg-white border border-red-200 text-red-700 hover:border-red-400 rounded-lg text-sm transition flex items-center gap-1"
                                  >
                                    <XCircle className="w-3 h-3" />
                                    Afwijzen
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Creator View - Limited proposals without profile links */}
            {proposalViewMode === 'creator' && proposals.length > 0 && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-[#3EB489]" />
                    {totalProposals} creators hebben gereageerd
                  </h2>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {proposals.slice(0, 5).map((proposal) => (
                      <div
                        key={proposal.id}
                        className="p-3 rounded-lg bg-[#FAFAF7] border border-[#E5E7EB] text-center select-none"
                      >
                        {proposal.creator.profilePictureUrl ? (
                          <img
                            src={proposal.creator.profilePictureUrl}
                            alt={proposal.creator.firstName}
                            className="w-12 h-12 rounded-full object-cover mx-auto mb-2"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#3EB489] flex items-center justify-center mx-auto mb-2">
                            <User className="w-6 h-6 text-white" />
                          </div>
                        )}
                        <p className="text-sm font-medium truncate">{proposal.creator.firstName}</p>
                        {proposal.creator.location?.city && (
                          <p className="text-xs text-[#475569]">{proposal.creator.location.city}</p>
                        )}
                        {proposal.creator.niches && proposal.creator.niches.length > 0 && (
                          <div className="flex flex-wrap justify-center gap-1 mt-1">
                            {proposal.creator.niches.slice(0, 2).map((niche, idx) => (
                              <span key={idx} className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] text-[#475569]">
                                {niche}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {totalProposals > 5 && (
                    <p className="text-center text-sm text-[#475569] mt-3">
                      en nog {totalProposals - 5} anderen...
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Public Proposals Preview - For non-logged-in users */}
            {proposalViewMode === 'public' && proposals.length > 0 && (
              <div className="relative">
                <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-[#3EB489]" />
                    Geïnteresseerde Creators ({proposals.length})
                  </h2>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {proposals.slice(0, 6).map((proposal) => {
                      const cardContent = (
                        <>
                          {proposal.creator.profilePictureUrl ? (
                            <img
                              src={proposal.creator.profilePictureUrl}
                              alt={proposal.creator.name}
                              className="w-12 h-12 rounded-full object-cover mx-auto mb-2"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-[#3EB489] flex items-center justify-center mx-auto mb-2">
                              <User className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <p className="text-sm font-medium truncate">{proposal.creator.name}</p>
                          {proposal.creator.location?.city && (
                            <p className="text-xs text-[#475569]">{proposal.creator.location.city}</p>
                          )}
                        </>
                      )
                      return onNavigate ? (
                        <button
                          key={proposal.id}
                          onClick={() => onNavigate('creator-detail', proposal.creator.id)}
                          className="p-3 rounded-lg bg-[#FAFAF7] border border-[#E5E7EB] hover:border-[#3EB489]/50 transition text-center"
                        >
                          {cardContent}
                        </button>
                      ) : (
                        <Link
                          key={proposal.id}
                          to={`/creators/${proposal.creator.id}`}
                          className="p-3 rounded-lg bg-[#FAFAF7] border border-[#E5E7EB] hover:border-[#3EB489]/50 transition text-center"
                        >
                          {cardContent}
                        </Link>
                      )
                    })}
                  </div>

                  {proposals.length > 6 && (
                    <p className="text-center text-sm text-[#475569] mt-3">
                      En nog {proposals.length - 6} anderen...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Budget Card */}
            <div className="relative">
              <div className="relative p-6 rounded-2xl bg-gradient-to-br from-[#ECFDF5] to-white border border-[#D1FAE5] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-2">Budget</h3>
                <div className="text-3xl font-black text-[#0A0F0D] mb-1 leading-none">
                  €{(project.budget * 1.21).toFixed(2).replace('.', ',')}
                </div>
                <p className="text-[#64748B] text-sm">
                  Inclusief 21% btw
                  {/* Ook excl. tonen: creators bieden excl. BTW, dus zonder dit bedrag
                      nemen ze het incl.-getal over en bieden ze 21% te hoog. */}
                  <span className="block text-xs text-[#94A3B8] mt-1">
                    €{project.budget.toLocaleString('nl-NL')} excl. btw
                  </span>
                </p>
                <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-xs font-medium bg-white border border-[#E5E7EB] text-[#1F2937]">
                  {project.budgetType === 'negotiable' ? 'Onderhandelbaar' : 'Vast budget'}
                </span>
              </div>
            </div>

            {/* Action Card */}
            <div className="relative scroll-mt-24" id="project-actions">
              <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                <h3 className="text-lg font-bold mb-4">Geïnteresseerd?</h3>

                {user?.role === 'creator' ? (
                  <>
                    {myProposal ? (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-[#475569] leading-relaxed">
                            <strong className="text-[#0F172A]">Je hebt al op deze opdracht gereageerd.</strong>
                            {myProposal.submittedAt && (
                              <> Ingediend op {new Date(myProposal.submittedAt).toLocaleDateString('nl-NL')}.</>
                            )}
                            {' '}Het bedrijf laat je weten zodra er nieuws is.
                          </p>
                        </div>
                        <button
                          onClick={() => handleNav('/my-proposals')}
                          className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-white border border-[#E5E7EB] rounded-xl font-semibold hover:border-[#3EB489] transition-all text-[#0F172A]"
                        >
                          Bekijk je voorstel
                        </button>
                      </div>
                    ) : !heeftPortfolioVideo ? (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-[#475569] leading-relaxed">
                            Om te kunnen reageren heb je minimaal{' '}
                            <strong className="text-[#0F172A]">één video in je portfolio</strong> nodig.
                            Bedrijven kiezen op basis van je werk — zonder voorbeeld maak je vrijwel geen kans.
                          </p>
                        </div>
                        <button
                          onClick={gaNaarPortfolio}
                          className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] hover:-translate-y-0.5 transition-all text-white shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]"
                        >
                          Video toevoegen aan portfolio
                        </button>
                      </div>
                    ) : !showProposalForm ? (
                      <button
                        onClick={() => setShowProposalForm(true)}
                        className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] hover:-translate-y-0.5 transition-all text-white shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Stuur voorstel
                      </button>
                    ) : (
                      <form onSubmit={handleProposalSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Motivatie <span className="text-[#475569]">(minimaal 50 karakters)</span>
                          </label>
                          <textarea
                            value={proposalData.coverLetter}
                            onChange={(e) => setProposalData({...proposalData, coverLetter: e.target.value})}
                            required
                            minLength="50"
                            maxLength="1000"
                            rows="4"
                            className="w-full px-4 py-3 bg-[#FAFAF7] border border-[#E5E7EB] rounded-xl focus:border-[#3EB489] focus:outline-none transition"
                            placeholder="Waarom ben jij perfect voor dit project? Beschrijf je ervaring en aanpak..."
                          />
                          <div className="text-right text-xs text-[#475569]">
                            {proposalData.coverLetter.length}/1000 karakters
                          </div>
                        </div>

                        {/* Het brief-budget staat incl. BTW op de pagina, maar dit veld is
                            excl. BTW. Zonder dat erbij te zeggen nemen creators het
                            budgetbedrag letterlijk over en bieden ze ongemerkt 21% te hoog. */}
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Jouw prijs (€) <span className="text-[#475569]">excl. BTW</span>
                          </label>
                          <input
                            type="number"
                            value={proposalData.price}
                            onChange={(e) => setProposalData({...proposalData, price: e.target.value})}
                            required
                            min="1"
                            className="w-full px-4 py-3 bg-[#FAFAF7] border border-[#E5E7EB] rounded-xl focus:border-[#3EB489] focus:outline-none transition"
                            placeholder="250"
                          />
                          {Number(proposalData.price) > 0 && (
                            <p className="mt-2 text-xs text-[#64748B]">
                              Het bedrijf ziet <strong className="text-[#0F172A]">
                                €{(Number(proposalData.price) * 1.21).toFixed(2).replace('.', ',')} incl. BTW
                              </strong>
                              {project?.budget && Number(proposalData.price) > project.budget && (
                                <span className="text-amber-700 font-semibold">
                                  {' '}— dat is boven hun budget van €{(project.budget * 1.21).toFixed(2).replace('.', ',')} incl. BTW
                                </span>
                              )}
                            </p>
                          )}
                          {project?.budget && (
                            <p className="mt-1 text-xs text-[#94A3B8]">
                              Budget van deze opdracht: €{project.budget.toLocaleString('nl-NL')} excl. BTW
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Levertijd (dagen)</label>
                          <input
                            type="number"
                            value={proposalData.deliveryTime}
                            onChange={(e) => setProposalData({...proposalData, deliveryTime: e.target.value})}
                            required
                            min="1"
                            className="w-full px-4 py-3 bg-[#FAFAF7] border border-[#E5E7EB] rounded-xl focus:border-[#3EB489] focus:outline-none transition"
                            placeholder="7"
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 px-4 py-2 bg-[#3EB489] hover:bg-[#359E78] rounded-lg transition disabled:opacity-50 text-white"
                          >
                            {submitting ? 'Verzenden...' : 'Verzend'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowProposalForm(false)}
                            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-[#FAFAF7] rounded-lg transition"
                          >
                            Annuleer
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                ) : (user?.role === 'bedrijf' || user?.role === 'agency') ? (
                  /* Eerder stond hier alleen "Ga naar je dashboard om voorstellen te
                     bekijken" — zonder link, en ingesloten in het dashboard verwees dat
                     naar de plek waar je al was. Nu een knop die er direct heen gaat. */
                  <div className="space-y-4">
                    <p className="text-[#475569]">
                      Dit is jouw opdracht. Creators kunnen erop reageren met een voorstel.
                    </p>
                    <button
                      onClick={() => handleNav(`/projects/${id}/proposals`)}
                      className="block w-full text-center px-6 py-3 bg-[#3EB489] hover:bg-[#359E78] rounded-xl transition text-white font-medium"
                    >
                      Voorstellen bekijken
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-[#475569]">
                      Log in als creator om een voorstel in te dienen.
                    </p>
                    <Link to="/login" className="block w-full text-center px-6 py-3 bg-[#3EB489] hover:bg-[#359E78] rounded-xl transition text-white no-underline">
                      Inloggen
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Card */}
            <div className="relative">
              <div className="relative p-6 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_10px_30px_-18px_rgba(0,0,0,0.12)]">
                <h3 className="text-lg font-bold mb-4">Statistieken</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#475569]">Views</span>
                    <span className="font-medium">{project.stats?.views || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#475569]">Voorstellen</span>
                    <span className="font-medium">{project.stats?.proposalsCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#475569]">Status</span>
                    <span className="px-2 py-1 bg-emerald-50 text-emerald-600 rounded text-sm">
                      {{
                        draft: 'Concept', open: 'Open', proposal_accepted: 'Wacht op aanbetaling',
                        awaiting_deposit: 'Wacht op aanbetaling', creator_selected: 'Creator gekozen',
                        awaiting_product_delivery: 'Product onderweg', in_progress: 'In uitvoering',
                        delivered: 'Opgeleverd', under_review: 'In beoordeling',
                        revision_requested: 'Revisie gevraagd', approved: 'Goedgekeurd',
                        completed: 'Voltooid', cancelled: 'Geannuleerd'
                      }[project.status] || project.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobiele sticky actiebalk (#20): op <lg staat de actiekaart pas helemaal onderaan */}
      {!onNavigate && (
        <div className="fixed bottom-0 left-0 right-0 z-[60] lg:hidden bg-white border-t border-[#E5E7EB] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-wide text-[#64748B] font-semibold">Budget</div>
              <div className="text-base font-black text-[#0A0F0D] leading-tight truncate">
                {typeof project.budget === 'number' ? `€${(project.budget * 1.21).toFixed(2).replace('.', ',')}` : '—'}
                <span className="ml-1 text-xs font-medium text-[#64748B]">incl. btw</span>
              </div>
            </div>
            {user?.role === 'creator' && myProposal ? (
              <button
                type="button"
                onClick={() => handleNav('/my-proposals')}
                className="min-h-[48px] px-5 bg-white border border-[#E5E7EB] rounded-xl font-semibold text-[#0F172A] text-sm inline-flex items-center justify-center gap-2 transition whitespace-nowrap"
              >
                <CheckCircle className="w-4 h-4 text-[#3EB489]" />
                Al gereageerd
              </button>
            ) : user?.role === 'creator' ? (
              <button
                type="button"
                onClick={() => {
                  if (heeftPortfolioVideo && !showProposalForm) setShowProposalForm(true)
                  document.getElementById('project-actions')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className="min-h-[48px] px-5 bg-[#3EB489] hover:bg-[#359E78] rounded-xl font-semibold text-white text-sm inline-flex items-center justify-center gap-2 transition whitespace-nowrap"
              >
                <Send className="w-4 h-4" />
                Stuur voorstel
              </button>
            ) : (user?.role === 'bedrijf' || user?.role === 'agency') ? (
              <button
                type="button"
                onClick={() => handleNav(`/projects/${id}/proposals`)}
                className="min-h-[48px] px-5 bg-[#3EB489] hover:bg-[#359E78] rounded-xl font-semibold text-white text-sm inline-flex items-center justify-center transition whitespace-nowrap"
              >
                Voorstellen bekijken
              </button>
            ) : (
              <Link
                to="/login"
                className="min-h-[48px] px-5 bg-[#3EB489] hover:bg-[#359E78] rounded-xl font-semibold text-white text-sm inline-flex items-center justify-center transition whitespace-nowrap no-underline"
              >
                Inloggen om te reageren
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Chat Dialog */}
      <ChatDialog
        isOpen={showChatDialog}
        onClose={() => setShowChatDialog(false)}
        creator={selectedCreator}
        project={project}
      />
    </div>
  )
}

export default ProjectDetailPage